import React, { useEffect, useMemo, useRef, useState } from "react";
import { loadCaiDiscoverySettings, refreshCaiDiscovery, saveCaiDiscoverySettings } from "../../lib/caiDiscoverySettingsApi";

type Run = { run_id: string; status?: string; run_date?: string; completed_at?: string | null };
type TargetDiagnostic = { url: string | null; outcome: string; httpStatus?: number; contentType?: string };
type Summary = {
  acceptedConfiguredUrlCount: number;
  discoveredAssetCount: number;
  preservedReviewCount: number;
  pagesAwaitingReviewCount: number;
  rejectedOrUnavailableUrlCount: number;
  configuredTargetUrlAcceptedCount: number;
  configuredTargetPagesObservedCount: number;
  configuredTargetPagesRejectedOrUnavailableCount: number;
  candidateRowsProcessedCount: number;
  newlyInsertedCandidateCount: number;
  existingCandidateRowsRediscoveredCount: number;
  candidatePagesAwaitingReviewCount: number;
  configuredTargetDiagnostics: TargetDiagnostic[];
};
const emptySummary: Summary = {
  acceptedConfiguredUrlCount: 0,
  discoveredAssetCount: 0,
  preservedReviewCount: 0,
  pagesAwaitingReviewCount: 0,
  rejectedOrUnavailableUrlCount: 0,
  configuredTargetUrlAcceptedCount: 0,
  configuredTargetPagesObservedCount: 0,
  configuredTargetPagesRejectedOrUnavailableCount: 0,
  candidateRowsProcessedCount: 0,
  newlyInsertedCandidateCount: 0,
  existingCandidateRowsRediscoveredCount: 0,
  candidatePagesAwaitingReviewCount: 0,
  configuredTargetDiagnostics: [],
};
const parseLines = (value: string) => value.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
const buttonStyle = { border: "1px solid #cbd5e1", background: "white", color: "#0f172a", borderRadius: 999, padding: "9px 13px", fontSize: 13, fontWeight: 800, cursor: "pointer" } as const;
const disabledButtonStyle = { ...buttonStyle, color: "#64748b", background: "#f1f5f9", cursor: "not-allowed", opacity: 0.75 } as const;

export default function CaiDiscoverySourcesPanel({ accountId, runs, onRefreshed }: { accountId: string; runs: Run[]; onRefreshed: () => void }) {
  const currentRun = useMemo(() => [...runs].filter((run) => run.status === "completed").sort((a, b) => `${b.completed_at || ""}|${b.run_date || ""}`.localeCompare(`${a.completed_at || ""}|${a.run_date || ""}`))[0] || null, [runs]);
  const [sitemapText, setSitemapText] = useState("");
  const [targetText, setTargetText] = useState("");
  const [savedSitemapText, setSavedSitemapText] = useState("");
  const [savedTargetText, setSavedTargetText] = useState("");
  const [status, setStatus] = useState("Loading CAI discovery settings…");
  const [summary, setSummary] = useState<Summary | null>(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const inFlight = useRef(false);
  const mounted = useRef(true);
  const dirty = sitemapText !== savedSitemapText || targetText !== savedTargetText;

  useEffect(() => {
    let active = true;
    mounted.current = true;
    inFlight.current = false;
    setSitemapText(""); setTargetText(""); setSavedSitemapText(""); setSavedTargetText("");
    setSummary(null); setBusy(false); setLoading(true); setStatus("Loading CAI discovery settings…");
    loadCaiDiscoverySettings(accountId).then((result) => {
      if (!active) return;
      const sitemap = Array.isArray(result.settings?.sitemapUrls) ? result.settings.sitemapUrls.join("\n") : "";
      const targets = Array.isArray(result.settings?.targetAssetUrls) ? result.settings.targetAssetUrls.join("\n") : "";
      setSitemapText(sitemap); setTargetText(targets); setSavedSitemapText(sitemap); setSavedTargetText(targets);
      setStatus("CAI discovery settings loaded.");
    }).catch((error) => { if (active) setStatus(error instanceof Error ? error.message : "CAI discovery settings are unavailable."); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; mounted.current = false; };
  }, [accountId]);

  async function saveSettings() {
    if (inFlight.current || loading) return;
    inFlight.current = true; setBusy(true); setStatus("Saving CAI settings…"); setSummary(null);
    try {
      const result = await saveCaiDiscoverySettings(accountId, parseLines(sitemapText), parseLines(targetText));
      if (!mounted.current) return;
      const sitemap = result.settings.sitemapUrls.join("\n");
      const targets = result.settings.targetAssetUrls.join("\n");
      setSitemapText(sitemap); setTargetText(targets); setSavedSitemapText(sitemap); setSavedTargetText(targets);
      setStatus("CAI settings saved successfully.");
    } catch (error) { if (mounted.current) setStatus(error instanceof Error ? error.message : "CAI settings could not be saved."); }
    finally { inFlight.current = false; if (mounted.current) setBusy(false); }
  }

  async function refreshDiscovery() {
    if (inFlight.current || !currentRun || dirty || loading) return;
    inFlight.current = true; setBusy(true); setStatus("Refreshing discovery from the current completed analysis run…"); setSummary(null);
    try {
      const result = await refreshCaiDiscovery(accountId, currentRun.run_id);
      if (!mounted.current) return;
      const counts = { ...emptySummary, ...(result.diagnostics || {}) } as Summary;
      setSummary(counts);
      setStatus(result.outcome === "partial"
        ? "Refresh partially completed. One or more configured target pages could not be observed; target results are summarized below. Any discovered pages remain unreviewed until an operator inspects and approves them."
        : result.status === "no_eligible_pages"
          ? "Refresh completed. No eligible pages were found; unavailable or rejected URLs are summarized below."
          : "Refresh completed. Discovered pages remain unreviewed until an operator inspects and approves them.");
      onRefreshed();
    } catch (error) { if (mounted.current) setStatus(error instanceof Error ? error.message : "CAI discovery refresh is unavailable."); }
    finally { inFlight.current = false; if (mounted.current) setBusy(false); }
  }

  return <section aria-label="CAI Discovery Sources" style={{ marginTop: 14, padding: 14, border: "1px solid #cbd5e1", borderRadius: 12, background: "#fbfdff" }}>
    <h3>CAI Discovery Sources</h3>
    <p>Configure bounded, account-owned discovery inputs. Adding a page does not verify its content or approve it.</p>
    <label style={{ display: "grid", gap: 4, marginTop: 10 }}>
      Sitemap URLs
      <textarea aria-label="Sitemap URLs" value={sitemapText} onChange={(event) => setSitemapText(event.target.value)} rows={4} disabled={busy || loading} />
      <span>Optional sitemap URLs to help CAI discover your website content. Enter one URL per line. Automatic sitemap discovery remains enabled. Maximum eight URLs.</span>
    </label>
    <label style={{ display: "grid", gap: 4, marginTop: 10 }}>
      Target Page URLs
      <textarea aria-label="Target Page URLs" value={targetText} onChange={(event) => setTargetText(event.target.value)} rows={4} disabled={busy || loading} />
      <span>Optional high-priority pages for CAI to discover and evaluate. Enter one URL per line. Adding a URL does not approve its content. Maximum 20 URLs.</span>
    </label>
    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 12 }}>
      <button type="button" onClick={saveSettings} disabled={busy || loading || !dirty} style={busy || loading || !dirty ? disabledButtonStyle : buttonStyle}>{busy ? "Working…" : "Save CAI Settings"}</button>
      <button type="button" onClick={refreshDiscovery} disabled={busy || loading || dirty || !currentRun} style={busy || loading || dirty || !currentRun ? disabledButtonStyle : buttonStyle}>Refresh CAI Discovery</button>
      <span>{currentRun ? `Current completed run: ${currentRun.run_date || currentRun.completed_at?.slice(0, 10) || currentRun.run_id}` : "Complete an analysis run before refreshing discovery."}</span>
    </div>
    <p role="status" aria-live="polite" style={{ marginTop: 10 }}>{status}</p>
    {summary ? <ul aria-label="Discovery refresh results">
      <li>{summary.acceptedConfiguredUrlCount} configured URLs accepted</li>
      <li>{summary.discoveredAssetCount} assets discovered or reconciled</li>
      <li>{summary.configuredTargetUrlAcceptedCount} configured target URLs accepted</li>
      <li>{summary.configuredTargetPagesObservedCount} configured target pages observed</li>
      <li>{summary.configuredTargetPagesRejectedOrUnavailableCount} configured target pages unavailable or rejected</li>
      <li>{summary.candidateRowsProcessedCount} candidate rows processed</li>
      <li>{summary.newlyInsertedCandidateCount} new candidate rows inserted</li>
      <li>{summary.existingCandidateRowsRediscoveredCount} existing candidate rows rediscovered</li>
      <li>{summary.pagesAwaitingReviewCount} pages awaiting operator review</li>
      <li>{summary.preservedReviewCount} existing reviews preserved</li>
      <li>{summary.rejectedOrUnavailableUrlCount} URLs rejected or unavailable</li>
      {summary.configuredTargetDiagnostics.length ? <li>
        Configured target results:
        <ul>{summary.configuredTargetDiagnostics.map((item, index) => <li key={`${item.url || "target"}-${index}`}>
          {item.url || `Configured target ${index + 1}`}: {item.outcome}{item.httpStatus ? ` (HTTP ${item.httpStatus})` : ""}{item.contentType ? ` — ${item.contentType}` : ""}
        </li>)}</ul>
      </li> : null}
    </ul> : null}
  </section>;
}
